import React, { useEffect, useRef } from "react";
import { EditorState, ContentState, convertFromHTML } from "draft-js";
import { Editor } from "react-draft-wysiwyg";
import { stateToHTML } from "draft-js-export-html";
import Immutable from "immutable";
import "draft-js/dist/Draft.css";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import "./SkEditor.css";
import MDBox from "components/MDBox";
import PropTypes from "prop-types";
import MDButton from "components/MDButton";
import axios from "axios";
import { handleAlert } from "redux/festures/alertSlice";
import { skCompany } from "redux/festures/isCompany";
import { useDispatch } from "react-redux";

export const exportOptions = {
  inlineStyles: {
    UNDERLINE: { element: "u" },
    STRIKETHROUGH: { element: "del" },
    SUPERSCRIPT: { element: "sup" },
    SUBSCRIPT: { element: "sub" },
  },
  inlineStyleFn: (styles) => {
    let styleObj = {};
    styles.forEach((style) => {
      if (style.startsWith("color-")) {
        styleObj.color = style.replace("color-", "");
      } else if (style.startsWith("bgcolor-")) {
        styleObj.backgroundColor = style.replace("bgcolor-", "");
      } else if (style.startsWith("fontsize-")) {
        styleObj.fontSize = style.replace("fontsize-", "") + "px";
      } else if (style.startsWith("fontfamily-")) {
        styleObj.fontFamily = style.replace("fontfamily-", "");
      }
    });
    if (Object.keys(styleObj).length > 0) {
      return {
        element: "span",
        style: styleObj,
      };
    }
  },
  blockStyleFn: (block) => {
    const align = block.getData() && block.getData().get("text-align");
    if (align) {
      return {
        style: {
          textAlign: align,
        },
      };
    }
  },
};

export const convertContentToHTML = (editorOrContentState) => {
  try {
    if (!editorOrContentState) return "";
    const contentState = typeof editorOrContentState.getCurrentContent === "function"
      ? editorOrContentState.getCurrentContent()
      : editorOrContentState;
    if (!contentState) return "";
    return stateToHTML(contentState, exportOptions);
  } catch (err) {
    console.error("Error converting Draft content to HTML:", err);
    return "";
  }
};

const Skeditor = ({
  editorState,
  setEditorState,
  placeholder,
  initialContent,
  content,
  isButton,
}) => {
  const admin = localStorage.getItem("admin_id");
  const dispatch = useDispatch();
  const lastLoadedContent = useRef(null);

  useEffect(() => {
    if (initialContent && initialContent !== lastLoadedContent.current) {
      lastLoadedContent.current = initialContent;
      try {
        const trimmed = typeof initialContent === "string" ? initialContent.trim() : "";
        if (!trimmed || trimmed === "<p><br></p>" || trimmed === "<p></p>") {
          setEditorState(EditorState.createEmpty());
          return;
        }

        const hasTag = /<[a-z][\s\S]*>/i.test(trimmed);
        const safeHtml = hasTag ? trimmed : `<p>${trimmed}</p>`;

        const blocksFromHTML = convertFromHTML(safeHtml);
        if (blocksFromHTML && blocksFromHTML.contentBlocks && blocksFromHTML.contentBlocks.length > 0) {
          let childNodes = [];
          try {
            if (typeof document !== "undefined") {
              const tempDiv = document.createElement("div");
              tempDiv.innerHTML = safeHtml;
              childNodes = Array.from(tempDiv.children);
            }
          } catch (e) {
            // Document unavailable fallback
          }

          const updatedBlocks = blocksFromHTML.contentBlocks.map((block, idx) => {
            const el = childNodes[idx];
            const align = el && el.style && el.style.textAlign;
            if (align) {
              return block.set("data", Immutable.Map({ "text-align": align }));
            }
            return block;
          });

          const contentState = ContentState.createFromBlockArray(
            updatedBlocks,
            blocksFromHTML.entityMap
          );
          const newEditorState = EditorState.createWithContent(contentState);
          setEditorState(newEditorState);
        }
      } catch (err) {
        console.error("Error parsing HTML into draft-js in Skeditor:", err);
      }
    } else if (!initialContent && !lastLoadedContent.current && (!editorState || !editorState.getCurrentContent)) {
      setEditorState(EditorState.createEmpty());
    }
  }, [initialContent]);

  const onChangeEditor = (state) => {
    setEditorState(state);
  };

  const handleSubmitForm = async () => {
    const htmlString = convertContentToHTML(editorState);
    const formData = new FormData();
    formData.append(content, htmlString);

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_APII}/update/company/${admin}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: localStorage.getItem("token"),
          },
        }
      );
      dispatch(
        handleAlert({
          isOpen: true,
          type: response?.data?.success ? "success" : "error",
          msg: response?.data?.message,
        })
      );
      dispatch(skCompany({ url: `/getCompanyByAdmin/${admin}` }));
    } catch (error) {
      console.log(error, "responseERROR");
    }
  };

  return (
    <MDBox sx={{ width: "100%" }}>
      <MDBox
        sx={{
          width: "100%",
          borderRadius: "8px",
          minHeight: "260px",
        }}
      >
        <Editor
          editorState={editorState}
          onEditorStateChange={onChangeEditor}
          placeholder={placeholder || "Type content here..."}
          wrapperClassName="sk-editor-wrapper"
          toolbarClassName="sk-editor-toolbar"
          editorClassName="sk-editor-main"
          toolbar={{
            options: [
              "inline",
              "blockType",
              "fontSize",
              "fontFamily",
              "list",
              "textAlign",
              "colorPicker",
              "link",
              "embedded",
              "emoji",
              "image",
              "remove",
              "history",
            ],
            inline: {
              inDropdown: false,
              options: [
                "bold",
                "italic",
                "underline",
                "strikethrough",
                "monospace",
                "superscript",
                "subscript",
              ],
            },
            blockType: {
              inDropdown: true,
              options: ["Normal", "H1", "H2", "H3", "H4", "H5", "H6", "Blockquote", "Code"],
            },
            fontSize: {
              options: [8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48, 60, 72],
            },
            fontFamily: {
              options: [
                "Arial",
                "Georgia",
                "Impact",
                "Tahoma",
                "Times New Roman",
                "Verdana",
                "Roboto",
                "Open Sans",
              ],
            },
            list: {
              inDropdown: false,
              options: ["unordered", "ordered", "indent", "outdent"],
            },
            textAlign: {
              inDropdown: false,
              options: ["left", "center", "right", "justify"],
            },
            history: {
              inDropdown: false,
              options: ["undo", "redo"],
            },
          }}
        />
      </MDBox>
      {!isButton && (
        <MDBox
          sx={{ display: "flex", alignItems: "flex-end", justifyContent: "flex-end", my: 1.5 }}
        >
          <MDButton color="info" variant="gradient" onClick={handleSubmitForm}>
            update
          </MDButton>
        </MDBox>
      )}
    </MDBox>
  );
};

export default Skeditor;

Skeditor.propTypes = {
  editorState: PropTypes.object,
  setEditorState: PropTypes.func,
  handleSubmitForm: PropTypes.func,
  initialContent: PropTypes.string,
  content: PropTypes.string,
  placeholder: PropTypes.string,
  isButton: PropTypes.bool,
};
